import React, { memo, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { noop } from 'shell/utils/noop';
import { ItemCtaVisibility } from 'system/components/shared/list-item-cta-menu/types';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { ActValueDomainVM } from 'civil-status/view-models/act-value-domain-managment-list';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useParams } from 'react-router-dom';
import { AxiosPromise } from 'axios';
import { useEventCallback } from '@mui/material';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { ValueDomainModalOpenType } from '../form/constants';
import {
  ValueDomainModalType,
  ValueDomainTableFiltersVM,
} from 'civil-status/view-models/act-value-domain';
import { useFilterTable } from 'shell/hooks/use-filter-table';

interface DomainValuesTableComponentProps {
  body: Array<ActValueDomainVM>;
  count: number;
  isForDetail?: boolean;
  isForEdit?: boolean;
  setDomainValueModal: React.Dispatch<React.SetStateAction<ValueDomainModalType>>;
  domainValueModal: ValueDomainModalType;
  setChoosenValueActDomain: (cell: ActValueDomainVM) => void;
  isForCreate?: boolean;
  onDeleteFE: (cell: ActValueDomainVM) => void;
  valuesDomainListBE: Array<ActValueDomainVM>;
}
const DomainValuesTableComponent = ({
  body = [],
  count,

  isForDetail = false,
  setDomainValueModal,
  domainValueModal,
  isForCreate = false,
  setChoosenValueActDomain,
  onDeleteFE,
  valuesDomainListBE = [],
}: DomainValuesTableComponentProps) => {
  const deleteValueDomain = useHttpClient(`${__ACT_API_URL__}/valori-dominio-atti/delete`, {
    options: {
      method: 'DELETE',
    },
  });

  const params = useParams();
  const tableData: Array<ActValueDomainVM> = useMemo(() => {
    let hidden: ItemCtaVisibility;

    if (isForDetail) {
      hidden = {
        read: false,
        edit: true,
        delete: true,
      };
    } else {
      hidden = {
        read: false,
        edit: false,
        delete: false,
      };
    }

    return body?.map((item) => {
      return {
        ...item,
        hidden,
      };
    });
  }, [body, isForDetail]);

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<ActValueDomainVM>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<ActValueDomainVM>>({
    element: null,
    open: false,
  });

  const deleteFE = useEventCallback(() => {
    if (isForDetail || !deleteAlertModal?.element) return;
    onDeleteFE(deleteAlertModal?.element);
    setDeleteAlertModal({ open: false, element: null });
    setDeleteSuccessModal({ open: true, element: deleteAlertModal?.element });
  });

  const onYesDeleteAlertModal = useEventCallback(() => {
    if (isForDetail || !deleteAlertModal?.element) return;

    if (isForCreate) {
      deleteFE();
    } else {
      //check if the value is in the backend array
      const isValueAlreadyExists = valuesDomainListBE?.find(
        (item) => item?.value === deleteAlertModal?.element?.value
      );

      if (isValueAlreadyExists) {
        (
          deleteValueDomain({
            url: `/${params?.id}/${deleteAlertModal?.element?.value}`,
          }) as AxiosPromise<void>
        ).then(() => {
          deleteFE();
        });
      } else {
        deleteFE();
      }
    }
  });

  const filterTable = useFilterTable<ValueDomainTableFiltersVM>();

  return (
    <>
      <Table
        onSort={noop}
        head={headCells}
        page={filterTable?.values?.page || 0}
        orders={{}}
        rowsPerPage={filterTable?.values?.rowsPerPage || 10}
        body={tableData}
        count={count}
        ariaLabel="Valori Domini"
        onPageChange={(value: number) => {
          filterTable.setValues({
            orders: filterTable.values?.orders || {},
            rowsPerPage: filterTable.values?.rowsPerPage || 10,
            page: value,
          });
        }}
        onVisibilityPress={(cell) => {
          return new Promise((resolve) => {
            setChoosenValueActDomain(cell);

            setDomainValueModal({
              open: !domainValueModal.open,
              type: ValueDomainModalOpenType.Read,
            });

            resolve(cell);
          }) as Promise<ActValueDomainVM>;
        }}
        onEditPress={(cell) => {
          return new Promise((resolve) => {
            setChoosenValueActDomain(cell);

            setDomainValueModal({
              open: !domainValueModal.open,
              type: ValueDomainModalOpenType.Edit,
            });

            resolve(cell);
          }) as Promise<ActValueDomainVM>;
        }}
        isServer={false}
        onTrashPress={(cell) => {
          return new Promise((resolve) => {
            setDeleteAlertModal({ open: true, element: cell });
            resolve(cell);
          }) as Promise<ActValueDomainVM>;
        }}
        noSort
        onRowsPerPageChange={(value: number) => {
          filterTable.setValues({
            orders: filterTable.values?.orders || {},
            page: 0,
            rowsPerPage: value,
          });
        }}
      />
      {deleteAlertModal?.open ? (
        <DeleteAlertModal
          title="Cancellazione del valore di dominio"
          message={`Confermare la cancellazione del valore ${
            deleteAlertModal.element?.value
          } di dominio  ${deleteAlertModal.element?.domainName || ''}?`}
          open={deleteAlertModal.open}
          onClose={() => setDeleteAlertModal({ open: false, element: null })}
          onYes={() => onYesDeleteAlertModal()}
          onNo={() => setDeleteAlertModal({ open: false, element: null })}
        />
      ) : undefined}
      {deleteSuccessModal?.open ? (
        <DeleteSuccessModal
          open={deleteSuccessModal.open}
          title="Cancellazione del valore di dominio"
          message={`la cancellazione del valore ${deleteSuccessModal.element?.value} di dominio  ${
            deleteSuccessModal.element?.domainName || ''
          } è avvenuto con successo`}
          onClose={() => {
            setDeleteSuccessModal({ open: false, element: null });
          }}
          onOk={() => {
            setDeleteSuccessModal({ open: false, element: null });
          }}
        />
      ) : undefined}
    </>
  );
};
DomainValuesTableComponent.displayName = 'Domain values table component';
const DomainValuesTable = memo(DomainValuesTableComponent);
export default DomainValuesTable;
